// @ts-nocheck
import i18next, { type Resource, type i18n as Ii18n } from "i18next";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import { ReadonlyVal, Val } from "value-enhancer";
import { SideEffectManager } from "side-effect-manager";
import en from "../locales/en.json";
import zhCN from "../locales/zh-CN.json";
import zhHant from "../locales/zh-Hant.json";
import koKR from "../locales/ko-KR.json";
import ar from "../locales/ar.json";

import varsCNen from "../vars/cn/en.json";
import varsCNzhCN from "../vars/cn/zh-CN.json";
import varsCNzhHant from "../vars/cn/zh-Hant.json";
import { initReactI18next } from "react-i18next";

export { Trans } from "react-i18next";
// import varsUSen from "../vars/us/en.json";
// import varsUSzhCN from "../vars/us/zh-CN.json";

/**
 * @param key - key of the translation
 * @param options - optional options for interpolation
 *
 * @example
 * i18n translation: { "hello": "Hello, {name}" }
 * tFunction("hello", { name: "John" })
 */
export type RemoI18nTFunction = (
    key: string,
    options?: Record<string, string | number | undefined>,
) => string;

export type RemoI18nLanguages = (typeof RemoI18n.languages)[number];
export enum RemoI18nLanguagesEnum {
    en = "en",
    zhCN = "zh-CN",
    zhHant = "zh-Hant",
    KoKR = "ko-KR",
    ar = "ar",
}

/**
 * NOTE: to use this library, also install https://github.com/antfu/i18n-ally
 * to edit translations. open a new vscode window at <path to renderer-app>.
 */

declare global {
    interface Window {
        __ReMoI18n?: RemoI18n;
    }
}

export class RemoI18n {
    public static t: RemoI18nTFunction = (key, options) =>
        RemoI18n.getInstance().i18n.t(key, options);

    public static changeLanguage = async (lang: RemoI18nLanguages): Promise<void> => {
        await RemoI18n.getInstance().i18n.changeLanguage(lang);
    };

    public static getInstance(): RemoI18n {
        return (window.__ReMoI18n ||= new RemoI18n());
    }

    public static readonly languages = Object.values(RemoI18nLanguagesEnum) as const;

    public readonly $Val: {
        readonly isReady$: ReadonlyVal<boolean>;
        readonly language$: ReadonlyVal<RemoI18nLanguages>;
    };

    // @ts-ignore
    public get isReady(): boolean {
        return this.$Val.isReady$.value;
    }

    // @ts-ignore
    public get language(): RemoI18nLanguages {
        return this.$Val.language$.value;
    }

    public t: RemoI18nTFunction = (key, options) => this.i18n.t(key, options);

    public changeLanguage = async (lang: RemoI18nLanguages): Promise<void> => {
        await this.i18n.changeLanguage(lang);
    };

    public readonly i18n: Ii18n;

    private _sideEffect = new SideEffectManager();

    private constructor() {
        const isReady$ = new Val(false);
        const language$ = new Val<RemoI18nLanguages>("en");
        this.$Val = { isReady$, language$ };

        this.i18n = i18next;

        const resources: Resource = {
            "en": { translation: en },
            "zh-CN": { translation: zhCN },
            "zh-Hant": { translation: zhHant },
            "ko-KR": { translation: koKR },
            "ar": { translation: ar },
        };

        const defaultVars: Record<string, Record<string, string>> = {
            "en": varsCNen,
            "zh-CN": varsCNzhCN,
            "zh-Hant": varsCNzhHant,
            "koKR": varsCNen,
            "ar": varsCNen,
        };

        i18next
            .use(initReactI18next)
            .use(I18nextBrowserLanguageDetector)
            .init({
                resources,
                fallbackLng: "en",
                supportedLngs: RemoI18n.languages,
                interpolation: {
                    escapeValue: false, // react already safes from xss
                    defaultVariables: defaultVars[i18next.language] || defaultVars.en,
                },
            })
            .then(() => {
                language$.setValue(i18next.language as RemoI18nLanguages);
                isReady$.setValue(true);
            });

        const changeLang = (lang: RemoI18nLanguages): void => {
            const htmlElement = document.querySelector("html");
            if (htmlElement) {
                htmlElement.setAttribute("lang", lang);
                // 处理阿拉伯语的从右到左布局
                if (lang === "ar") {
                    htmlElement.setAttribute("dir", "rtl");
                    htmlElement.classList.add("ar-lang");
                } else {
                    htmlElement.setAttribute("dir", "ltr");
                    htmlElement.classList.remove("ar-lang");
                }
            }

            const defaultVariables = defaultVars[lang] || defaultVars.en;
            if (i18next.options.interpolation) {
                i18next.options.interpolation.defaultVariables = defaultVariables;
            } else {
                i18next.options.interpolation = { defaultVariables };
            }

            language$.setValue(lang);
        };

        changeLang(i18next.language as RemoI18nLanguages);

        i18next.on("languageChanged", changeLang);
        this._sideEffect.addDisposer(() => {
            i18next.off("languageChanged", changeLang);
        });
    }

    public destroy(): void {
        this._sideEffect.flushAll();
    }
}
